import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import { Row, Col, Button, Skeleton, DatePicker, Form, Alert } from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import LoginChecked from "../Shared/LoginChecked";
import { NumberFormat } from "../Shared/Utility";
import { parseReportAmount } from "../../Pdf/PrintFormateNumber";
import { IdentificationIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import moment from "moment";

const CURRENT_ASSET_ITEMS = [
  { label: "Customer receivable", key: "customerReceivable" },
  { label: "Supplier receivable", key: "supplierReceivable" },
  { label: "Other receivables", key: "otherReceivable" },
  { label: "Inventory (at cost)", key: "totalStockValue" },
  { label: "Cash in hand", key: "totalCashInHand" },
  { label: "Cash at bank", key: "totalCashAtBank" },
];

const CURRENT_LIABILITY_ITEMS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Other payables", key: "otherPayable" },
];

function WorkingCapital() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [asOfDate, setAsOfDate] = useState(dayjs());

  useEffect(() => {
    document.title = "Working Capital";
    setLoading(true);

    axios({
      method: "post",
      url: config.base_url + "Report/GetWorkingCapitalReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        Dated: dayjs(asOfDate).format("YYYY-MM-DD"),
      },
    })
      .then((response) => {
        if (response.data.status_code == 1) {
          setReportData(response.data);
        } else {
          setReportData({});
        }
        setLoading(false);
      })
      .catch(() => {
        setReportData({});
        setLoading(false);
      });
  }, [asOfDate, UserID, AccessKey]);

  const fmt = (val) => NumberFormat(parseReportAmount(val), 0);

  const totals = useMemo(() => {
    const totalCurrentAssets =
      parseReportAmount(reportData.totalCurrentAssets) ||
      CURRENT_ASSET_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(reportData[item.key]),
        0
      );
    const totalCurrentLiabilities =
      parseReportAmount(reportData.totalCurrentLiabilities) ||
      CURRENT_LIABILITY_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(reportData[item.key]),
        0
      );
    const workingCapital =
      parseReportAmount(reportData.workingCapital) ||
      totalCurrentAssets - totalCurrentLiabilities;
    const currentRatio =
      parseReportAmount(reportData.currentRatio) ||
      (totalCurrentLiabilities > 0
        ? totalCurrentAssets / totalCurrentLiabilities
        : 0);
    return {
      totalCurrentAssets,
      totalCurrentLiabilities,
      workingCapital,
      currentRatio,
    };
  }, [reportData]);

  const asOfLabel = moment(asOfDate).format(config.date_format);
  const isPositive = totals.workingCapital >= 0;

  const disabledDate = (current) =>
    current && current > moment().endOf("day");

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Working Capital</h3>
            <div className="header-actions">
              <NavLink
                to={`/summary-report/print-working-capital?AsOfDate=${dayjs(
                  asOfDate
                ).format("YYYY-MM-DD")}`}
                target="_blank"
              >
                <Button
                  type="dashed"
                  shape="round"
                  size="large"
                  icon={<PrinterOutlined />}
                >
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <div className="filter-wrap">
            <Form layout="inline">
              <Form.Item label="As of date">
                <DatePicker
                  value={asOfDate}
                  onChange={(date) => date && setAsOfDate(date)}
                  disabledDate={disabledDate}
                  allowClear={false}
                />
              </Form.Item>
            </Form>
          </div>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Working capital as of ${asOfLabel}`}
            description="Working capital = Current assets − Current liabilities. It measures short-term liquidity available to run daily operations."
          />

          {loading ? (
            <Row>
              <Col xs={24} md={16}>
                <Skeleton active />
                <Skeleton active />
              </Col>
            </Row>
          ) : (
            <Row justify="center" gutter={[24, 24]}>
              <Col md={10} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <IdentificationIcon />
                      </span>
                      <h4>Current Assets</h4>
                    </div>
                  </div>
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      {CURRENT_ASSET_ITEMS.map((item) => (
                        <li key={item.key}>
                          <h4>{item.label}</h4>
                          <p>{fmt(reportData[item.key])}</p>
                        </li>
                      ))}
                      <li>
                        <h4 style={{ color: "#405289" }}>Total current assets</h4>
                        <p className="netprofit">
                          {fmt(totals.totalCurrentAssets)}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>

              <Col md={10} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <IdentificationIcon />
                      </span>
                      <h4>Current Liabilities</h4>
                    </div>
                  </div>
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      {CURRENT_LIABILITY_ITEMS.map((item) => (
                        <li key={item.key}>
                          <h4>{item.label}</h4>
                          <p>{fmt(reportData[item.key])}</p>
                        </li>
                      ))}
                      <li>
                        <h4 style={{ color: "#405289" }}>
                          Total current liabilities
                        </h4>
                        <p className="netprofit">
                          {fmt(totals.totalCurrentLiabilities)}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>

              <Col md={20} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      <li>
                        <h4
                          style={{
                            color: isPositive ? "rgb(85 162 107)" : "#ec0d0d",
                          }}
                        >
                          Working capital (current assets − current liabilities)
                        </h4>
                        <p className={isPositive ? "profit" : "loss"}>
                          {fmt(totals.workingCapital)}
                        </p>
                      </li>
                      <li>
                        <h4>Current ratio (assets ÷ liabilities)</h4>
                        <p>
                          {totals.totalCurrentLiabilities > 0
                            ? totals.currentRatio.toFixed(2)
                            : "—"}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </>
  );
}

export default WorkingCapital;
